import { useCallback, useContext } from "react";
import { useQueries } from "@tanstack/react-query";
import apiRequest from "api/apiRequest";
import { UserContext } from "context/user";

const fetchZones = async (query, token) => {
  try {
    const response = await apiRequest.get(`v2/zoning/zones?${query}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    });
    return response.data.zoned_image;
  } catch (error) {
    throw new Error(error);
  }
};

export const useZonesQuery = (query, fieldIds) => {
  const { token } = useContext(UserContext);

  const results = useQueries({
    queries: fieldIds.map((fieldId) => ({
      queryKey: ["zones", query, fieldId, token],
      queryFn: () => fetchZones(`${query}&field_ids=${fieldId}`, token),
      enabled: false,
      retry: 1,
      staleTime: 86400000,
    })),
  });

  const refetchAll = useCallback(() => {
    results.forEach((result) => result.refetch());
  }, [results]);

  let featureCounter = 1;

  const mergedFeatures = results.reduce((merged, result) => {
    const { features } = result.data || {};

    if (!features || !merged) return null;

    const updatedFeatures = features.map((feature) => {
      const updatedFeature = { ...feature, id: featureCounter };
      featureCounter += 1;

      return updatedFeature;
    });

    return merged.concat(updatedFeatures);
  }, []);

  const isLoading = results.some((result) => result.fetchStatus === "fetching");

  const error = results.some((result) => !!result.error);

  const isStale = results.some((result) => result.isStale);

  if (mergedFeatures === null) {
    return { data: null, error, isLoading, refetchAll, isStale };
  }

  const mergedFeatureCollection = {
    type: "FeatureCollection",
    features: mergedFeatures,
  };

  return {
    data: mergedFeatureCollection,
    error: false,
    isLoading: false,
    isStale,
    refetchAll,
  };
};
